/**
 * Statement — formelle Tragfähigkeitsstellungnahme.
 *
 * Stellungnahmen sind audit-pflichtig (10 Jahre bei B2G). Jede Änderung
 * wird als neue Revision gespeichert; Revisionen sind unveränderlich
 * (append-only). Status-Wechsel `draft → review → signed` sind monoton —
 * eine signierte Stellungnahme darf nicht zurück in `draft`.
 *
 * `aiAssisted` markiert ob ein LLM zur Texterstellung beigetragen hat —
 * Pflichtangabe nach EU-AI-Act / Transparenzpflicht. `signedBy` ist die
 * fachkundige Person, die die Stellungnahme inhaltlich verantwortet.
 */
import { z } from 'zod';
import { ScoreResultSchema } from './score.js';
import { CaseIdSchema, JobcenterIdSchema, StatementIdSchema, UserIdSchema } from './ids.js';

export const StatementStatusSchema = z.enum(['draft', 'review', 'signed']);
export type StatementStatus = z.infer<typeof StatementStatusSchema>;

export const StatementRecommendationSchema = z.enum([
  'tragfaehig', // grün
  'eingeschraenkt_tragfaehig', // gelb
  'nicht_tragfaehig', // rot
  'nicht_abschliessend', // Konfidenz < 60%
]);
export type StatementRecommendation = z.infer<typeof StatementRecommendationSchema>;

export const StatementRecipientSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('jobcenter'),
    jobcenterId: JobcenterIdSchema,
    jobcenterName: z.string().min(1).max(200),
    fileNumber: z.string().min(1).max(64),
  }),
  z.object({
    type: z.literal('mandant'),
    salutation: z.string().min(1).max(100),
  }),
]);
export type StatementRecipient = z.infer<typeof StatementRecipientSchema>;

export const StatementRevisionSchema = z.object({
  revisionNumber: z.number().int().min(1),
  authorUserId: UserIdSchema,
  body: z.string().min(1).max(50_000),
  recommendation: StatementRecommendationSchema,
  /** Snapshot des Score-Stands zur Revision (immutability) */
  scoreSnapshot: ScoreResultSchema,
  aiAssisted: z.boolean(),
  /** Falls AI-assisted: welches Modell? */
  aiModelIdentifier: z.string().max(200).optional(),
  createdAt: z.coerce.date(),
});
export type StatementRevision = z.infer<typeof StatementRevisionSchema>;

export const StatementSchema = z
  .object({
    id: StatementIdSchema,
    caseId: CaseIdSchema,
    status: StatementStatusSchema,
    recipient: StatementRecipientSchema,
    /** Append-only — neueste Revision = letzte */
    revisions: z.array(StatementRevisionSchema).min(1),
    signedBy: UserIdSchema.optional(),
    signedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .refine((s) => (s.status === 'signed' ? s.signedBy !== undefined && !!s.signedAt : true), {
    message: 'Signierte Stellungnahme braucht signedBy + signedAt',
    path: ['status'],
  })
  .refine((s) => s.revisions.every((r, idx) => r.revisionNumber === idx + 1), {
    message: 'Revisionen müssen lückenlos 1..N nummeriert sein',
    path: ['revisions'],
  });
export type Statement = z.infer<typeof StatementSchema>;
